import { t, type StaffLanguage } from './staffText'

/**
 * Putting a set-aside customer back in line.
 *
 * The Set aside panel is a list and not a to-do list, and that is deliberate: a
 * pooled customer comes back through the desk's own Call next, chosen by the
 * branch's ordinary rules like anybody else. A Put back button on every row would
 * teach an operator to reach past the mechanism that works, and the mechanism
 * working is the whole reason pools were built.
 *
 * There is one customer the mechanism does not reach from where the operator is
 * standing: the one this desk cannot call. The panel's own neighbour says it —
 * closing a desk on somebody already in service parks them, "nobody's line holds
 * them until a supervisor puts them back". Until now no screen offered the action
 * at all, though the server has had it all along.
 *
 * So the rule is two conditions and not one, and both have to hold.
 */

/** What a supervisor holds that an operator does not. Spelled out here rather
 *  than imported from the screen, because this file is the one a test can run. */
export const SUPERVISE = 'ticket.supervise'

/**
 * Whether this row offers Put back in line.
 *
 * `comesBackOnCallNext` is the panel's own "yours" — whether this desk's profile
 * draws from the pool the customer is sitting in. Where it does, the customer is
 * already on their way back and the button would only be a faster way of doing
 * the wrong thing: it takes them out of the pool somebody put them in, ahead of
 * whoever else is waiting in it.
 */
export function offersPutBack(
  permissions: readonly string[],
  comesBackOnCallNext: boolean,
): boolean {
  if (comesBackOnCallNext) return false
  return permissions.includes(SUPERVISE)
}

/**
 * What the operator is asked before it fires.
 *
 * The number is in the question because the answer is irreversible from this
 * screen and the rows are a column of numbers that look alike. "Put A-014 back in
 * line?" is a sentence somebody can check against the person in front of them;
 * "Are you sure?" is not.
 */
export function putBackQuestion(displayNumber: string, lang: StaffLanguage): string {
  return t('floor.putBackQuestion', lang, { number: displayNumber })
}

/**
 * Why it is being offered, in the words the panel says it in.
 *
 * One sentence, and it names the pool rather than the ticket state, because the
 * pool is what the operator can see on the row above.
 */
export function putBackNote(lang: StaffLanguage): string {
  return t('floor.putBackNote', lang)
}
